import { t, i18n, textIn } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';
import { img } from '../ui/render.js';
import { pickText, heroUrl } from '../core/hero-slides.js';

/* Cabecera del inicio cuando no hay películas ni series que destacar (solo canales): un carrusel de pantallas que
   cuentan qué es Filoteca. Cada pantalla es un objeto { kind: 'promo', item: { id }, … } con los textos ya en el idioma
   del usuario. Las de serie están aquí; el servidor puede cambiarlas, añadir otras y poner publicidad (server/hero.js):
   una pantalla del servidor con el mismo `id` que una de serie hereda de ella lo que no traiga. */

/** Ajustes de diseño que viajan con la pantalla (ver server/hero.js LAYOUT). */
export const LAYOUT_KEYS = ['dim', 'sideX', 'sideY', 'sideSize', 'textX', 'textY', 'textW', 'titleSize', 'textSize'];

const BENEFITS = [['bolt', 'promo.b1'], ['verified_user', 'promo.b2'], ['devices', 'promo.b3'], ['tune', 'promo.b4']];

/** Pantallas de serie, con los textos ya traducidos. `canRecord`: la web con grabaciones lo cita entre las funciones. */
function builtins({ logos, canRecord }) {
    const features = [['live_tv', 'promo.f1'], ['movie_filter', 'promo.f2'], ['view_list', 'promo.f3']];
    if (canRecord) features.push(['cloud_download', 'promo.f4']);
    features.push(['manage_accounts', 'promo.f5']);
    const mk = (id, extra) => Object.assign({ kind: 'promo', item: { id }, logos, show: 'empty' }, extra);
    return [
        mk('tv', { icon: 'live_tv', art: 'tv', title: t('promo.tv.title'), accent: t('promo.tv.accent'), text: t('promo.tv.text'), features: features.map(([icon, k]) => ({ icon, text: t(k) })), benefits: BENEFITS.map(([icon, k]) => ({ icon, text: t(k) })), cta: { label: t('promo.tv.cta'), href: '/live/', icon: 'play_arrow' }, more: { label: t('promo.tv.more'), href: '/guide/' } }),
        mk('library', { icon: 'movie', art: 'library', title: t('promo.lib.title'), accent: t('promo.lib.accent'), text: t('promo.lib.text'), cta: { label: t('promo.lib.cta'), href: '/settings/?add=source', icon: 'add' } }),
        mk('guide', { icon: 'calendar_month', art: 'guide', title: t('promo.guide.title'), accent: t('promo.guide.accent'), text: t('promo.guide.text'), cta: { label: t('promo.guide.cta'), href: '/guide/', icon: 'calendar_month' } })
    ];
}

/** Los textos de serie de una pantalla (tv, library, guide) en todos los idiomas: lo que el panel enseña para editar. */
export function builtinTexts(id) {
    const key = { tv: 'tv', library: 'lib', guide: 'guide' }[id];
    if (!key) return null;
    const langs = ['es', 'en', 'fr', 'it', 'pt', 'de'];
    const all = (k) => Object.fromEntries(langs.map((l) => [l, textIn(l, k)]));
    const out = { title: all('promo.' + key + '.title'), accent: all('promo.' + key + '.accent'), text: all('promo.' + key + '.text'), cta: { label: all('promo.' + key + '.cta'), href: { tv: '/live/', library: '/settings/?add=source', guide: '/guide/' }[id] }, features: [], benefits: [] };
    if (id === 'tv') {
        out.more = { label: all('promo.tv.more'), href: '/guide/' };
        // Sin «Grabaciones»: solo existe en la web; quien edite puede añadirla.
        out.features = [['live_tv', 'promo.f1'], ['movie_filter', 'promo.f2'], ['view_list', 'promo.f3'], ['manage_accounts', 'promo.f5']].map(([icon, k]) => ({ icon, text: all(k) }));
        out.benefits = BENEFITS.map(([icon, k]) => ({ icon, text: all(k) }));
    }
    return out;
}

const link = (l, lang) => (l && l.href ? { label: pickText(l.label, lang), href: l.href, icon: l.icon || '' } : null);
const list = (xs, lang) => (xs || []).map((x) => ({ icon: x.icon, text: pickText(x.text, lang) }));

/**
 * Las pantallas del carrusel. `remote` son las del servidor: si hay alguna, son las que se enseñan (en su orden); si
 * no, las de serie. `logos` son logos de canales del usuario (para el mosaico de la tele).
 */
export function promoSlides({ logos = [], remote = [], canRecord = false, lang = i18n.lang } = {}) {
    const base = builtins({ logos, canRecord });
    if (!remote.length) return base;
    return remote.map((r) => {
        const b = base.find((x) => x.item.id === r.id) || { kind: 'promo', item: { id: r.id }, icon: 'campaign', art: 'tv', logos };
        const out = Object.assign({}, b, { item: { id: r.id }, ad: !!r.ad, show: r.show === 'always' ? 'always' : 'empty', image: heroUrl(r.image), imageMobile: heroUrl(r.imageMobile) });
        ['kicker', 'title', 'accent', 'text'].forEach((k) => { if (r[k]) out[k] = pickText(r[k], lang); });
        LAYOUT_KEYS.forEach((k) => { if (r[k] != null && Number.isFinite(Number(r[k]))) out[k] = Number(r[k]); });
        if (r.features) out.features = list(r.features, lang);
        if (r.benefits) out.benefits = list(r.benefits, lang);
        if (r.cta) out.cta = link(r.cta, lang);
        // El servidor guarda para esta pantalla «/settings/» a secas: el botón «Añadir proveedor» abre Ajustes → Fuentes con el asistente.
        if (r.id === 'library' && out.cta && /^\/settings\/?$/.test(String(out.cta.href))) out.cta = Object.assign({}, out.cta, { href: '/settings/?add=source' });
        if (r.more) out.more = link(r.more, lang);
        return out;
    });
}

const tile = (logo) => `<span class="promo-tile">${logo ? img(logo, '') : '<span class="mi">tv</span>'}</span>`;

const benefitsHtml = (s) => (s.benefits && s.benefits.length ? `<ul class="promo-benefits">${s.benefits.map((b) => `<li><span class="mi">${escapeHtml(b.icon)}</span>${escapeHtml(b.text)}</li>`).join('')}</ul>` : '');

function artHtml(s) {
    if (s.image) return benefitsHtml(s) ? `<div class="promo-art promo-art-side" aria-hidden="true">${benefitsHtml(s)}</div>` : '';
    const logos = (s.logos || []).slice(0, 6);
    if (s.art === 'library') {
        return `<div class="promo-art promo-art-library" aria-hidden="true">
            ${['movie', 'tv', 'theaters', 'star'].map((icon, i) => `<span class="promo-poster is-${i}"><span class="mi">${icon}</span></span>`).join('')}</div>`;
    }
    if (s.art === 'guide') {
        const rows = Array.from({ length: 4 }, (_, i) => `<div class="promo-epg-row">${tile(logos[i] || '')}<span class="promo-bar" style="width:${[46, 62, 38, 54][i]}%"></span><span class="promo-bar is-next" style="width:${[30, 22, 36, 26][i]}%"></span></div>`).join('');
        return `<div class="promo-art promo-art-guide" aria-hidden="true"><div class="promo-epg">${rows}</div></div>`;
    }
    const tiles = Array.from({ length: 6 }, (_, i) => tile(logos[i] || '')).join('');
    return `<div class="promo-art promo-art-tv" aria-hidden="true"><div class="promo-tv"><div class="promo-tv-screen">${tiles}</div></div>${benefitsHtml(s)}</div>`;
}

/** Botón o enlace: una ruta de la app navega dentro; una dirección https se abre aparte. */
function linkHtml(l, cls, nav) {
    if (!l) return '';
    const icon = l.icon ? `<span class="mi">${escapeHtml(l.icon)}</span>` : '';
    const attrs = /^https:/.test(l.href) ? `href="${escapeHtml(l.href)}" target="_blank" rel="noopener sponsored"` : `href="#" data-href="${escapeHtml(l.href)}"`;
    return `<a class="btn ${cls}" ${attrs} ${nav ? 'data-nav-default' : ''}>${icon}${escapeHtml(l.label)}</a>`;
}

/** HTML de una pantalla del carrusel (misma estructura que un destacado: fondo, contenido, flechas y puntos). */
export function promoHtml(list, index) {
    const s = list[index];
    const many = list.length > 1;
    const bg = s.image
        ? `<div class="hero-bg promo-img">${s.imageMobile ? `<picture><source media="(max-width: 699px)" srcset="${escapeHtml(s.imageMobile)}"/><img src="${escapeHtml(s.image)}" alt="" decoding="async"/></picture>` : `<img src="${escapeHtml(s.image)}" alt="" decoding="async"/>`}</div>`
        : `<div class="hero-bg hero-fade promo-bg promo-bg-${escapeHtml(s.art || 'tv')}"></div>`;
    // Ajustes de diseño de la pantalla (del panel), como variables CSS de la sección.
    const num = (k, min, max) => (s[k] != null && Number.isFinite(Number(s[k])) ? Math.max(min, Math.min(max, Number(s[k]))) : null);
    const vars = [];
    const put = (k, min, max, fn) => { const v = num(k, min, max); if (v != null) vars.push(fn(v)); };
    put('dim', 0, 100, (v) => `--promo-dim:${v / 100}`);
    put('sideX', 0, 60, (v) => `--promo-side:${v}%`);
    put('sideY', 0, 100, (v) => `--promo-side-y:${v}%`);
    put('sideSize', 50, 200, (v) => `--promo-side-scale:${v / 100}`);
    put('textX', 0, 60, (v) => `--promo-text-x:${v}%`);
    put('textY', 0, 100, (v) => `--promo-text-y:${v}%`);
    put('textW', 20, 90, (v) => `--promo-text-w:${v}%`);
    put('titleSize', 50, 250, (v) => `--promo-title:${v / 100}`);
    put('textSize', 50, 200, (v) => `--promo-fs:${v / 100}`);
    const hasLayout = ['textX', 'textY', 'textW'].some((k) => num(k, 0, 100) != null);
    const style = vars.length ? `style="${vars.join(';')}"` : '';
    return `<section class="hero is-promo ${s.image ? 'has-image' : ''} ${hasLayout ? 'has-layout' : ''} ${many ? 'has-carousel' : ''}" data-featured="promo:${escapeHtml(s.item.id)}" ${style}>
        ${bg}
        ${artHtml(s)}
        ${s.ad ? `<span class="promo-ad">${escapeHtml(t('promo.ad'))}</span>` : ''}
        <div class="hero-content hero-fade">
            <div class="hero-kicker"><span class="mi">${escapeHtml(s.icon || 'live_tv')}</span>${escapeHtml(s.kicker || t('app.name'))}</div>
            <h1 class="hero-title">${escapeHtml(s.title || '')} <span class="promo-accent">${escapeHtml(s.accent || '')}</span></h1>
            ${s.text ? `<p class="hero-desc">${escapeHtml(s.text)}</p>` : ''}
            ${s.features && s.features.length ? `<ul class="promo-features">${s.features.map((f) => `<li><span class="mi">${escapeHtml(f.icon)}</span>${escapeHtml(f.text)}</li>`).join('')}</ul>` : ''}
            <div class="hero-actions">
                ${linkHtml(s.cta, 'btn-primary btn-lg', true)}
                ${linkHtml(s.more, 'btn-lg hero-secondary', false)}
            </div>
        </div>
        ${many ? `<button class="hero-arrow is-left" data-hero-step="-1" aria-label="${t('hero.prev')}" data-nav-skip><span class="mi">chevron_left</span></button>
        <button class="hero-arrow is-right" data-hero-step="1" aria-label="${t('hero.next')}" data-nav-skip><span class="mi">chevron_right</span></button>
        <div class="hero-dots" data-nav-row>${list.map((x, i) => `<button class="hero-dot ${i === index ? 'is-active' : ''}" data-hero-go="${i}" aria-label="${escapeHtml((x.title || '') + ' ' + (x.accent || ''))}"></button>`).join('')}</div>` : ''}
    </section>`;
}
